import React, { useCallback, useState, useEffect } from 'react';
import { Button, Row, Col, Form, Input } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ErrorMessage } from '@hookform/error-message';

import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import DefaultModal from 'components/Modal/DefaultModal';
import CustomerPortfolioService from '../../../../../../../SLA/CustomerPortfolio/service';
import { useUsers } from 'views/Report/Protocol/hooks/useUsers';
import CustomFieldsInputsForm from '../../../../../../shared/components/CustomFieldsInputsForm';

import { FlexController } from 'components/FlexControllerInput';
import FlexSelect from 'components/Inputs/FlexSelect';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import Loader from 'components/Loader';
import AccountService from '../../../../../services/accountService';
import ModalConfirmation from 'components/Modal/ConfirmationModal';

const BatchEditModal = ({
  isModalOpen,
  onModalToggle,
  accountIds,
  callback,
  selectedRecordTypeId,
  setSelectedRows,
  ...props
}) => {
  const [submitAttempt, setSubmitAttempt] = useState(false);
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [finalMessage, setFinalMessage] = useState('');

  const { control, handleSubmit, errors, setValue, watch, getValues } =
    useForm();

  const [customerPortfolios, setCustomerPortfolios] = useState([]);
  const { userList } = useUsers();

  const { getCustomFields, customFields, isLoadingCustomFields } =
    useEntityCustomFieldsByRecordType(
      entitiesName.ACCOUNT,
      selectedRecordTypeId
    );

  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  useEffect(() => {
    getCustomFields();
  }, [getCustomFields]);

  useEffect(() => {
    CustomerPortfolioService.getWithoutPagination()
      .then((response) => {
        const data = response.data.data.content.map((item) => ({
          name: item.name,
          id: item.id
        }));
        setCustomerPortfolios(data);
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.message ||
            err.message ||
            'Erro ao buscar categorias dos clientes'
        );
      });
  }, []);

  const handleUpdateAccountsInBatch = () => {
    const values = getValues();
    onSubmit(values);
  };

  const handleOpenModalConfirmation = () => {
    const values = getValues();
    const defaultFields = [
      { name: 'name', label: 'Nome' },
      { name: 'responsible', label: 'Responsável' },
      { name: 'phone', label: 'Telefone' },
      { name: 'customerPortfolioId', label: 'Categoria dos Clientes' }
    ];

    const filledData = Object.fromEntries(
      Object.entries(values).filter(
        ([_prop, value]) =>
          value !== '' && value !== undefined && value !== null
      )
    );

    const defaultFieldsLabelMap = new Map(
      defaultFields.map((field) => [field.name, field.label])
    );

    const modifiedDefaultFields = Object.entries(filledData)
      .filter(([key]) => defaultFieldsLabelMap.has(key))
      .map(
        ([key, value]) =>
          `<li><strong>${defaultFieldsLabelMap.get(
            key
          )} = ${value}</strong></li>`
      )
      .join('');

    const customFieldsLabelMap = new Map(
      customFields.map((field) => [field.name, field.label])
    );

    const modifiedCustomFields = Object.entries(filledData)
      .filter(([key]) => customFieldsLabelMap.has(key))
      .map(
        ([key, value]) =>
          `<li><strong>${customFieldsLabelMap.get(
            key
          )} = ${value}</strong></li>`
      )
      .join('');

    const textoFinal = `
      <div>
        <h3>Deseja continuar?</h3>
        <p>Campos que terão seus valores modificados:</p>

        <ul>
          ${modifiedDefaultFields}
          ${modifiedCustomFields}
        </ul>
      </div>
    `;
    setFinalMessage(textoFinal);
    setOpenModalConfirmation(true);
  };

  const onSubmit = async (data) => {
    setSubmitAttempt(true);

    const filledData = Object.fromEntries(
      Object.entries(data).filter(
        ([_prop, value]) =>
          value !== '' && value !== undefined && value !== null
      )
    );

    const idsToSubmit = accountIds.map((accountId) => accountId.id);

    const formattedData = {
      accountIds: idsToSubmit,
      recordTypeId: selectedRecordTypeId,
      data: filledData
    };

    try {
      await AccountService.batchEdit(formattedData);
      toast.success('Contas selecionadas foram atualizadas com sucesso');
      setSelectedRows([]);
      if (callback) callback();
      closeModal();
    } catch (error) {
      const errMsg =
        error.response?.data?.message || 'Erro ao editar Contas em lote';
      toast.error(errMsg);
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const closeModal = useCallback(() => onModalToggle(false), [onModalToggle]);

  if (isLoadingCustomFields) {
    return (
      <Row className="justify-content-md-center">
        <Loader />
      </Row>
    );
  }

  return (
    <DefaultModal
      show={isModalOpen}
      onModalToggle={onModalToggle}
      title="Edição em lote de Contas"
      showCloseButton={false}
      modalContainerClasses="modal-xl"
      {...props}
    >
      <Form
        className="mt--3"
        onSubmit={(event) => {
          event.stopPropagation();
          handleSubmit(onSubmit)(event);
        }}
      >
        <Row>
          <Col sm="6" className="mb-3">
            <label className="form-control-label text-capitalize">Nome</label>
            <Controller
              render={(props) => (
                <Input
                  value={props.value}
                  onChange={props?.onChange}
                  type="text"
                  placeholder="Nome"
                />
              )}
              control={control}
              defaultValue=""
              name="name"
            />
            <ErrorMessage
              errors={errors}
              name="name"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>

          <Col className="mb-3">
            <FlexController
              type="phone"
              label="Telefone"
              name="phone"
              disabled={false}
              showMessageButton={false}
              showCallButton={false}
              watch={watch}
              value={watch('phone')}
              control={control}
              errors={errors}
              rules={{ required: false }}
              validateOnSubmit={submitAttempt}
              valueController={(fieldName, value) => {
                setValue(fieldName, value);
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col sm="6" className="mb-3">
            <label className="form-control-label text-capitalize">
              Categoria dos Clientes
            </label>
            <Controller
              control={control}
              name="customerPortfolioId"
              render={(props) => (
                <FlexSelect
                  isClearable={true}
                  dataOptions={customerPortfolios || []}
                  isMulti={false}
                  closeMenuOnSelect={true}
                  value={props.value}
                  valueController={setValue}
                  fieldName="customerPortfolioId"
                  labelName="name"
                  valueName="id"
                  defaultValue={''}
                />
              )}
              defaultValue={''}
            />
            <ErrorMessage
              errors={errors}
              name="customerPortfolioId"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
          <Col sm="6">
            <label className="form-control-label">Responsável</label>
            <Controller
              render={(props) => (
                <FlexSelect
                  dataOptions={userList || []}
                  isMulti={false}
                  value={props.value}
                  valueController={setValue}
                  fieldName="responsible"
                  labelName="name"
                  valueName="login"
                  isClearable={true}
                  closeMenuOnSelect={true}
                />
              )}
              control={control}
              name="responsible"
            />
            <ErrorMessage
              errors={errors}
              name="responsible"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
        </Row>
        <Row className="mb-1">
          <CustomFieldsInputsForm
            fields={customFields}
            control={control}
            errors={errors}
            setValue={setValue}
            Controller={Controller}
            options={{
              showCallIconOnPhoneField: false,
              showMessageIconOnPhoneField: false
            }}
            contactMessageClientName={''}
            watch={watch}
            isUpdate={false}
            isOnEditPage={true}
          />
        </Row>

        <Row className="mt-1">
          <Col>
            <Button
              style={buttonStyle(systemConfiguration.confirmation_button_color)}
              onClick={handleOpenModalConfirmation}
            >
              Alterar
            </Button>
            <Button
              style={buttonStyle(systemConfiguration.cancelation_button_color)}
              onClick={closeModal}
            >
              Cancelar
            </Button>
          </Col>
        </Row>
      </Form>

      <ModalConfirmation
        isModalOpen={openModalConfirmation}
        onModalToggle={setOpenModalConfirmation}
        unmountOnClose={true}
        idResource={accountIds}
        deleteResource={handleUpdateAccountsInBatch}
        message={finalMessage}
        messageContainsHtml={true}
      />
    </DefaultModal>
  );
};

export default BatchEditModal;
